import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

// Admin area — publisher activation and revenue-share overrides.
//
// ⚠️ Activation is not cosmetic. A publisher registers with isActive:false, and
// that flag also gates the CORS allow-list and the proxy-login origin check — so
// until an admin flips it, the publisher's site cannot talk to WallPass at all.
// This page is the only way to do that without editing the database by hand.

const Card = ({ children, className = '' }) => (
  <div className={`bg-wp-surface rounded-xl border border-wp-border p-5 ${className}`}>{children}</div>
);

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const DEFAULT_FEE_HINT = 'platform default';

/** One row's revenue-share editor. Empty input clears the override. */
const RevShareCell = ({ publisher, onSave, busy }) => {
  const initial = publisher.revSharePercent === null || publisher.revSharePercent === undefined
    ? ''
    : String(publisher.revSharePercent);
  const [value, setValue] = useState(initial);
  const [error, setError] = useState(null);

  useEffect(() => { setValue(initial); }, [initial]);

  const dirty = value !== initial;

  const commit = () => {
    setError(null);
    const trimmed = value.trim();

    // Empty clears the override (null → fall back to the platform default).
    // That is deliberately distinct from 0, which means this publisher pays no
    // platform fee at all.
    if (trimmed === '') {
      onSave(publisher.id, { revSharePercent: null });
      return;
    }

    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
      setError('0–100, or blank');
      return;
    }
    onSave(publisher.id, { revSharePercent: parsed });
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="decimal"
          aria-label={`Revenue share percent for ${publisher.name}`}
          value={value}
          disabled={busy}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
          placeholder={DEFAULT_FEE_HINT}
          className="w-32 bg-wp-bg border border-wp-border rounded-md px-2 py-1 text-white text-sm
                     placeholder:text-wp-muted focus:outline-none focus:border-wp-accent disabled:opacity-50"
        />
        {dirty && (
          <button
            type="button"
            onClick={commit}
            disabled={busy}
            className="text-xs px-2 py-1 rounded-md bg-wp-accent text-black font-medium disabled:opacity-50"
          >
            Save
          </button>
        )}
      </div>
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
};

const AdminPage = () => {
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/publishers');
      setPublishers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load publishers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePublisher = useCallback(async (id, patch) => {
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      const res = await api.put(`/api/publishers/${id}`, patch);
      const updated = res.data?.publisher;
      // Patch in place rather than refetching, so an edit does not reorder or
      // scroll the table underneath whoever is working through it.
      setPublishers((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...(updated || patch) } : p))
      );
      setNotice('Saved.');
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed.');
    } finally {
      setBusyId(null);
    }
  }, []);

  const activeCount = publishers.filter((p) => p.isActive).length;
  const pending = publishers.filter((p) => !p.isActive);

  return (
    <div className="min-h-screen bg-wp-bg py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <header className="mb-6">
          <h1 className="text-white text-2xl font-semibold">Admin</h1>
          <p className="text-wp-muted text-sm mt-1">
            Publisher activation and revenue-share overrides.
          </p>
        </header>

        {pending.length > 0 && (
          <Card className="mb-5 border-wp-accent/40">
            <p className="text-white text-sm">
              <span className="font-semibold">{pending.length}</span>{' '}
              {pending.length === 1 ? 'publisher is' : 'publishers are'} awaiting activation.
              Until activated they cannot call the API or complete a login from their site.
            </p>
          </Card>
        )}

        {error && (
          <div role="alert" className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3">
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}
        {notice && !error && (
          <div role="status" className="mb-4 rounded-lg border border-wp-border bg-wp-surface px-4 py-2">
            <p className="text-wp-muted text-sm">{notice}</p>
          </div>
        )}

        <Card>
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-white text-lg font-semibold">Publishers</h2>
            <p className="text-wp-muted text-sm">
              {activeCount} active / {publishers.length} total
            </p>
          </div>

          {loading ? (
            <p className="text-wp-muted text-sm py-8 text-center">Loading…</p>
          ) : publishers.length === 0 ? (
            <p className="text-wp-muted text-sm py-8 text-center">No publishers yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">
                  Registered publishers with activation state and revenue-share override
                </caption>
                <thead>
                  <tr className="text-left text-wp-muted border-b border-wp-border">
                    <th scope="col" className="py-2 pr-4 font-medium">Publisher</th>
                    <th scope="col" className="py-2 pr-4 font-medium">Site</th>
                    <th scope="col" className="py-2 pr-4 font-medium">API key</th>
                    <th scope="col" className="py-2 pr-4 font-medium">Registered</th>
                    <th scope="col" className="py-2 pr-4 font-medium">Rev share %</th>
                    <th scope="col" className="py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {publishers.map((p) => (
                    <tr key={p.id} className="border-b border-wp-border/50 last:border-0">
                      <td className="py-3 pr-4 text-white">{p.name}</td>
                      <td className="py-3 pr-4 text-wp-muted">
                        {p.websiteUrl ? (
                          <a
                            href={p.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-white underline underline-offset-2"
                          >
                            {p.websiteUrl.replace(/^https?:\/\//, '')}
                          </a>
                        ) : '—'}
                      </td>
                      {/* Prefix only — the raw key is shown once at generation and never stored. */}
                      <td className="py-3 pr-4 text-wp-muted font-mono text-xs">{p.apiKeyPrefix || '—'}</td>
                      <td className="py-3 pr-4 text-wp-muted">{fmtDate(p.createdAt)}</td>
                      <td className="py-3 pr-4">
                        <RevShareCell publisher={p} onSave={savePublisher} busy={busyId === p.id} />
                      </td>
                      <td className="py-3">
                        <button
                          type="button"
                          disabled={busyId === p.id}
                          onClick={() => savePublisher(p.id, { isActive: !p.isActive })}
                          aria-label={`${p.isActive ? 'Deactivate' : 'Activate'} ${p.name}`}
                          className={`px-3 py-1 rounded-md text-xs font-medium disabled:opacity-50 ${
                            p.isActive
                              ? 'bg-wp-border text-white hover:bg-wp-border/70'
                              : 'bg-wp-accent text-black'
                          }`}
                        >
                          {busyId === p.id ? '…' : p.isActive ? 'Active — deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <p className="text-wp-muted text-xs mt-4">
          Blank revenue share means the platform default applies. Zero means this publisher pays
          no platform fee — the two are stored differently.
        </p>
      </div>
    </div>
  );
};

export default AdminPage;
